<script lang="ts" setup>
  import { computed } from 'vue';

  const props = defineProps({
    href: { type: String, required: false, default: '' },
    shadow: { type: Boolean, required: false, default: false },
    large: { type: Boolean, required: false, default: false },
    small: { type: Boolean, required: false, default: false },
    numeric: { type: Boolean, required: false, default: false },
    interactive: { type: Boolean, required: false, default: false },
    selected: { type: Boolean, required: false, default: false },
    default: { type: Boolean, required: false, default: false },
  });

  const tileClasses = computed(() => {
    return {
      'tile--small': props.small,
      'tile--large': props.large,
      'tile--shadow': props.shadow,
      'tile--numeric': props.numeric,
      'tile--interactive': props.interactive,
      'tile--selected': props.selected,
      'tile--default': props.default,
    };
  });

  const component = computed(() => {
    if (props.href) {
      return 'RouterLink';
    } else {
      return 'div';
    }
  });

  const specificProps = computed(() => ({
    href: props.href || undefined,
    tabindex: props.interactive || props.href ? 0 : undefined,
    role: props.interactive && !props.href ? 'button' : undefined,
  }));
</script>
<template>
  <component :is="component" :class="{ ...tileClasses }" class="tile" v-bind="{ ...specificProps }">
    <div v-if="$slots.outer" class="tile__outer">
      <slot name="outer" />
    </div>
    <div class="tile__content">
      <div v-if="$slots.title || $slots.headerAction" class="tile__header">
        <slot name="title" />
        <slot name="headerAction" />
      </div>
      <slot />
      <div v-if="$slots.footer" class="tile__footer">
        <slot name="footer" />
      </div>
    </div>
  </component>
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --sf-tile-outer-gap: 1rem;
    --sf-tile-outer-padding-y: 0rem;
    --sf-tile-outer-padding-x: 2rem;

    --sf-tile-transition-timing-func: ease-out;
    --sf-tile-transition-duration: 200ms;

    --sf-tile-padding: 2rem;
    --sf-tile-large-padding: 3rem;
    --sf-tile-small-padding: 1rem;

    --sf-tile-gap: 1rem;
    --sf-tile-large-gap: 2rem;
    --sf-tile-small-gap: 1rem;

    --sf-tile-background: var(--section-background);
    --sf-tile-box-shadow: none;
    --sf-tile-border-inline: var(--section-border-inline);
    --sf-tile-border-block: var(--section-border-block);
    --sf-tile-border-radius: var(--section-border-radius);

    --sf-tile-outer-background: var(--sf-tile-background);

    --sf-tile-interactive-background: var(--sf-tile-background);
    --sf-tile-interactive-box-shadow: var(--secondary-box-shadow-200);

    --sf-tile-interactive-background-hover: rgb(255, 255, 255);
    --sf-tile-interactive-box-shadow-hover: var(--primary-box-shadow-200);

    --sf-tile-interactive-border-color: transparent;
    --sf-tile-interactive-border-style: solid;
    --sf-tile-interactive-border-width: 1px;

    --sf-tile-interactive-border-color-hover: var(--primary-background);
    --sf-tile-interactive-border-style-hover: solid;
    --sf-tile-interactive-border-width-hover: 1px;

    --sf-tile-link-box-shadow: var(--secondary-box-shadow-200);
    --sf-tile-link-box-shadow-hover: var(--primary-box-shadow-200);
  }

  .tile {
    display: flex;
    flex-direction: column;
    gap: var(--sf-tile-gap);
    border-radius: var(--sf-tile-border-radius);
    scroll-margin: var(--sf-tile-outer-gap);
    box-shadow: var(--sf-tile-box-shadow);

    transition: background-color var(--sf-tile-transition-duration) var(--sf-tile-transition-timing-func),
    border var(--sf-tile-transition-duration) var(--sf-tile-transition-timing-func),
    box-shadow var(--sf-tile-transition-duration) var(--sf-tile-transition-timing-func),
    transform var(--sf-tile-transition-duration) var(--sf-tile-transition-timing-func),
    scale 0.1s ease;

    > .tile__outer {
      display: flex;
      align-items: center;
      gap: calc(var(--sf-tile-gap) * 0.75);
      padding: var(--sf-tile-outer-padding-y) var(--sf-tile-outer-padding-x);
    }

    > .tile__content {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: var(--sf-tile-gap);
      flex-grow: 1;
      background: var(--sf-tile-background);
      border-radius: var(--sf-tile-border-radius);
      border-inline: var(--sf-tile-border-inline);
      border-block: var(--sf-tile-border-block);
      padding: var(--sf-tile-padding);
      transition-property: background, border, box-shadow, transform;
      transition-timing-function: var(--sf-tile-transition-timing-func);
      transition-duration: var(--sf-tile-transition-duration);

      > .tile__header,
      > .tile__footer {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      > .tile__header {
        justify-content: space-between;
      }

      > .tile__footer {
        justify-content: flex-end;
      }
    }

    &--large {
      > .tile__outer {
        gap: var(--sf-tile-large-gap);
        padding-left: var(--sf-tile-large-padding);
      }

      > .tile__content {
        padding: var(--sf-tile-large-padding);
        gap: var(--sf-tile-large-gap);
      }
    }

    &--small {
      > .tile__outer {
        gap: var(--sf-tile-small-gap);
        padding-left: var(--sf-tile-small-padding);
      }

      > .tile__content {
        padding: var(--sf-tile-small-padding);
        gap: var(--sf-tile-small-gap);
      }
    }

    &--shadow {
      box-shadow: var(--sf-tile-link-box-shadow);
    }

    &--numeric {
      .tile__outer {
        padding-left: 0;
      }
    }

    &--interactive {
      cursor: pointer;
      background: var(--sf-tile-interactive-background);
      box-shadow: var(--sf-tile-interactive-box-shadow);
      border: var(--sf-tile-interactive-border-color);
      border-style: var(--sf-tile-interactive-border-style);
      border-width: var(--sf-tile-interactive-border-width);

      > .tile__content {
        border-inline: none;
        border-block: none;
        background: var(--sf-tile-interactive-background);
      }

      &:active {
        background-color: var(--sf-tile-interactive-background-hover);
        box-shadow: var(--sf-tile-interactive-box-shadow-hover);
        border: var(--sf-tile-interactive-border-color-hover);
        border-style: var(--sf-tile-interactive-border-style-hover);
        border-width: var(--sf-tile-interactive-border-width-hover);
        scale: 0.99;
      }

      @media(hover: hover) {
        &:hover {
          background-color: var(--sf-tile-interactive-background-hover);
          box-shadow: var(--sf-tile-interactive-box-shadow-hover);
          border: var(--sf-tile-interactive-border-color-hover);
          border-style: var(--sf-tile-interactive-border-style-hover);
          border-width: var(--sf-tile-interactive-border-width-hover);
        }
      }
    }

    &--selected {
      background-color: var(--sf-tile-interactive-background-hover);
      box-shadow: var(--sf-tile-interactive-box-shadow-hover);
      border: var(--sf-tile-interactive-border-color-hover);
      border-style: var(--sf-tile-interactive-border-style-hover);
      border-width: var(--sf-tile-interactive-border-width-hover);
    }

    &--default .tile__outer {
      position: absolute;
      top: 0;
      font-size: 0.875rem;
      font-weight: var(--font-weight-semi);
      line-height: 1.25rem;
      margin-left: 0.5rem;
      padding: 0 0.5rem;
      transform: translateY(-50%);
      background: var(--sf-tile-outer-background);
      border-radius: var(--sf-tile-border-radius);
    }

    .tile {
      .tile__content,
      .tile__outer {
        background: white;
      }
    }
  }

  a.tile {
    &:active {
      box-shadow: var(--sf-tile-link-box-shadow-hover);
    }

    @media(hover: hover) {
      &:hover {
        box-shadow: var(--sf-tile-link-box-shadow-hover);
      }
    }
  }

  @include media.query(lg) {
    .tile {
      //background: var(--sf-tile-background);

      > .tile__outer {
        //background: var(--sf-tile-background);
        padding: calc(var(--sf-tile-padding) / 3) 0 0 calc(var(--sf-tile-padding) / 3);
        flex-wrap: wrap;
      }

      > .tile__content {
        gap: calc(var(--sf-tile-gap) / 2);
        padding: calc(var(--sf-tile-padding) / 3);

        .tile {
          --sf-tile-border-radius: var(--border-radius);
        }
      }

      &--large {
        > .tile__outer {
          gap: var(--sf-tile-large-gap);
          padding-left: calc(var(--sf-tile-large-padding) / 3);
        }

        > .tile__content {
          padding: calc(var(--sf-tile-large-padding) / 2) calc(var(--sf-tile-large-padding) / 3);
          gap: calc(var(--sf-tile-large-gap) / 2);
        }
      }

      &--small {
        > .tile__outer {
          padding-left: var(--sf-tile-small-padding);
        }

        > .tile__content {
          padding: var(--sf-tile-small-padding);
          gap: calc(var(--sf-tile-small-gap) / 2);
        }
      }

      &--default {
        > .tile__outer {
          padding: 0 0.5rem;
        }
      }
    }

    .modal{
      .tile {
        --sf-tile-border-radius: var(--border-radius);
      }
    }
  }
</style>
